import { useMemo } from 'react';
import { useDeviceType, useMediaQuery } from '@vfit/shared/hooks';
import { colors, Colors, pxToLineHeightRem, pxToRem } from '@vfit/shared/themes';
import { IUseLink } from './link.models';
import { useCta } from '../../cta.hook';

export const useLink = <T>({
  _href,
  _onClick,
  _onMouseEnter,
  _onMouseLeave,
  _size,
  disableAppMobileOpenInBrowser,
  disabled,
  variant,
}: IUseLink<T>) => {
  const { isHover, href, onClick, onMouseEnter, onMouseLeave } = useCta<T>({
    _href,
    _onClick,
    _onMouseEnter,
    _onMouseLeave,
    disableAppMobileOpenInBrowser,
    disabled,
  });

  const isDesktop = useMediaQuery('desktop', 'min');
  const { isMobile } = useDeviceType();

  // If 'breadcrumb' or 'white' deafult size should be 'responsive'
  const size = _size || (variant !== 'regular' ? 'responsive' : undefined);

  // The 'white' variant doesn't change color on hover
  const color: Colors = useMemo(() => {
    switch (true) {
      case disabled:
        return colors.$7e7e7e;
      case variant === 'white':
        return colors.$ffffff;
      case variant === 'mva:turquoise':
        return colors.$007c92;
      case variant === 'breadcrumb':
        return !isHover ? colors.$262626 : colors.$bebebe;
      case variant === 'regular':
      default:
        return !isHover ? colors.$262626 : colors.$e60000;
    }
  }, [disabled, isHover, variant]);

  const height = useMemo(() => {
    switch (_size) {
      case 'big':
        return pxToLineHeightRem(18, 26);
      case 'responsive':
        return !isDesktop ? pxToLineHeightRem(14, 18) : pxToLineHeightRem(16, 22);
      case 'responsiveMedium':
        return isMobile ? pxToRem(20) : pxToRem(24);
      case 'small':
      default:
        return pxToLineHeightRem(16, 22);
    }
  }, [isDesktop, isMobile, _size]);

  return {
    color,
    height,
    href,
    onClick,
    onMouseEnter,
    onMouseLeave,
    size,
  };
};

